import { LitElement } from 'lit';

/**
 * Utility function to clone and extract slot content from a LitElement component
 * This is useful when you need to disable shadow DOM and render slot content directly
 *
 * @param element - The LitElement instance to extract children from
 * @returns Array of cloned child nodes
 */
export function extractSlotContent(element: LitElement): Node[] {
    const children = Array.from(element.childNodes).map((node) =>
        node.cloneNode(true)
    );
    return children;
}

/**
 * Utility function to clear all child nodes from a LitElement component
 * This is typically used before rendering new content when shadow DOM is disabled
 *
 * @param element - The LitElement instance to clear children from
 */
export function clearSlotContent(element: LitElement): void {
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
}

/**
 * Combined utility function that extracts and clears slot content in one call
 * This is the most common use case when dealing with slot content in components
 * without shadow DOM
 *
 * @param element - The LitElement instance to process
 * @returns Array of cloned child nodes
 */
export function processSlotContent(element: LitElement): Node[] {
    const children = extractSlotContent(element);
    clearSlotContent(element);
    return children;
}

/**
 * Utility function to check if an element has any slot content
 *
 * @param element - The LitElement instance to check
 * @returns Boolean indicating if the element has children
 */
export function hasSlotContent(element: LitElement): boolean {
    return element.childNodes.length > 0;
}

/**
 * Utility function to extract only text content from slot
 *
 * @param element - The LitElement instance to extract text from
 * @returns The text content as a string
 */
export function extractSlotTextContent(element: LitElement): string {
    return element.textContent?.trim() || '';
}

/**
 * Utility function to extract content from named slots
 *
 * @param element - The LitElement instance to extract from
 * @param slotName - The name of the slot to extract
 * @returns Array of nodes that have the specified slot attribute
 */
export function extractNamedSlotContent(
    element: LitElement,
    slotName: string
): Node[] {
    const slottedElements = Array.from(element.children).filter(
        (child) => child.getAttribute('slot') === slotName
    );
    return slottedElements.map((node) => node.cloneNode(true));
}

/**
 * Utility function to extract default slot content (elements without slot attribute)
 *
 * @param element - The LitElement instance to extract from
 * @returns Array of nodes that don't have a slot attribute
 */
export function extractDefaultSlotContent(element: LitElement): Node[] {
    const defaultElements = Array.from(element.childNodes).filter((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            return !(node as Element).hasAttribute('slot');
        }
        // Include text nodes and other non-element nodes in default slot
        return true;
    });
    return defaultElements.map((node) => node.cloneNode(true));
}

/**
 * Utility function to process slots with named slot support
 *
 * @param element - The LitElement instance to process
 * @returns Object containing named slots and default content
 */
export function processNamedSlots(element: LitElement): {
    [slotName: string]: Node[];
    default: Node[];
} {
    const slots: { [slotName: string]: Node[]; default: Node[] } = {
        default: [],
    };

    // Extract default slot content first
    slots.default = extractDefaultSlotContent(element);

    // Extract named slots
    const slottedElements = Array.from(element.children).filter((child) =>
        child.hasAttribute('slot')
    );

    slottedElements.forEach((child) => {
        const slotName = child.getAttribute('slot')!;
        if (!slots[slotName]) {
            slots[slotName] = [];
        }
        slots[slotName].push(child.cloneNode(true));
    });

    // Clear the element
    clearSlotContent(element);

    return slots;
}
